import { memo, useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { FilePreviewLoadingState } from "./FilePreviewLoadingState";
import { FilePreviewErrorState } from "./FilePreviewErrorState";
import {
  FILE_PREVIEW_MIN_HEIGHT,
  FILE_PREVIEW_IMAGE_MAX_WIDTH_MOBILE,
  FILE_PREVIEW_IMAGE_MAX_WIDTH_DESKTOP,
  FILE_PREVIEW_IMAGE_MAX_HEIGHT_MOBILE,
  FILE_PREVIEW_IMAGE_MAX_HEIGHT_DESKTOP,
} from "@/constants";

interface FilePreviewImagePreviewProps {
  src: string;
  alt?: string;
  maxWidth?: number;
  maxHeight?: number;
  className?: string;
}

export const FilePreviewImagePreview = memo(
  ({
    src,
    alt = "File preview",
    maxWidth,
    maxHeight,
    className = "",
  }: FilePreviewImagePreviewProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
      setLoading(true);
      setError(false);
    }, [src]);

    const handleLoad = useCallback(() => {
      setLoading(false);
      setError(false);
    }, []);

    const handleError = useCallback(() => {
      setLoading(false);
      setError(true);
    }, []);

    const containerStyle = useMemo(() => {
      const style: React.CSSProperties & Record<string, string> = {};

      if (maxWidth) {
        style.maxWidth = `${maxWidth}px`;
      } else {
        style["--max-width-mobile"] =
          `${FILE_PREVIEW_IMAGE_MAX_WIDTH_MOBILE}px`;
        style["--max-width-desktop"] =
          `${FILE_PREVIEW_IMAGE_MAX_WIDTH_DESKTOP}px`;
      }

      if (maxHeight) {
        style.maxHeight = `${maxHeight}px`;
      } else {
        style["--max-height-mobile"] =
          `${FILE_PREVIEW_IMAGE_MAX_HEIGHT_MOBILE}px`;
        style["--max-height-desktop"] =
          `${FILE_PREVIEW_IMAGE_MAX_HEIGHT_DESKTOP}px`;
      }

      if (loading || error) {
        style.minHeight = `${FILE_PREVIEW_MIN_HEIGHT}px`;
      }

      return style;
    }, [maxWidth, maxHeight, loading, error]);

    const loadingErrorStyle = useMemo(
      () => ({
        minHeight: `${FILE_PREVIEW_MIN_HEIGHT}px`,
        width: maxWidth ? `${maxWidth}px` : "100%",
      }),
      [maxWidth],
    );

    const imageStyle = useMemo(() => {
      const style: React.CSSProperties & Record<string, string> = {};

      if (!maxWidth) {
        style["--img-max-width-mobile"] =
          `${FILE_PREVIEW_IMAGE_MAX_WIDTH_MOBILE}px`;
        style["--img-max-width-desktop"] =
          `${FILE_PREVIEW_IMAGE_MAX_WIDTH_DESKTOP}px`;
      }

      if (!maxHeight) {
        style["--img-max-height-mobile"] =
          `${FILE_PREVIEW_IMAGE_MAX_HEIGHT_MOBILE}px`;
        style["--img-max-height-desktop"] =
          `${FILE_PREVIEW_IMAGE_MAX_HEIGHT_DESKTOP}px`;
      }

      return style;
    }, [maxWidth, maxHeight]);

    return (
      <div
        className={cn(
          "relative flex items-center justify-center rounded-lg mx-auto",
          "[&:not([style*='maxWidth'])]:max-w-[var(--max-width-mobile)] [&:not([style*='maxWidth'])]:md:max-w-[var(--max-width-desktop)]",
          "[&:not([style*='maxHeight'])]:max-h-[var(--max-height-mobile)] [&:not([style*='maxHeight'])]:md:max-h-[var(--max-height-desktop)]",
          (loading || error) && "w-full",
          !loading && !error && "w-fit",
          className,
        )}
        style={containerStyle}
      >
        {loading && (
          <FilePreviewLoadingState
            message={t("filePreview.loadingImage")}
            style={loadingErrorStyle}
            className="w-full"
          />
        )}

        {error && (
          <FilePreviewErrorState
            title={t("filePreview.errorLoadingImage")}
            message={t("filePreview.renderFileError")}
            style={loadingErrorStyle}
            className="w-full"
          />
        )}

        <img
          key={src}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={imageStyle}
          className={cn(
            "w-auto h-auto object-contain",
            maxWidth
              ? "max-w-full"
              : "max-w-[var(--img-max-width-mobile)] md:max-w-[var(--img-max-width-desktop)]",
            maxHeight
              ? "max-h-full"
              : "max-h-[var(--img-max-height-mobile)] md:max-h-[var(--img-max-height-desktop)]",
            (loading || error) && "hidden",
          )}
        />
      </div>
    );
  },
);
